<script setup lang="ts">
import { CV_COURSES_NOTE, type CvDocument } from '../stores/cv_versions'

// Read-only rendering of one structured CV document, section by section.
// This is the admin's review view, not the public layout; the renderer
// slice owns how it looks to a recruiter.
defineProps<{ document: CvDocument }>()

function period(start: string, end: string): string {
  if (!start && !end) return ''
  return `${start || '?'} – ${end || '?'}`
}
</script>

<template>
  <article class="flex flex-col gap-5 text-sm text-white/80" data-testid="cv-document">
    <section v-if="document.summary" data-testid="cv-summary">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-1">Summary</h3>
      <p class="m-0">{{ document.summary }}</p>
    </section>

    <section v-if="document.approach.length" data-testid="cv-approach">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-2">Leadership &amp; approach</h3>
      <ul class="m-0 pl-4 flex flex-col gap-1">
        <li v-for="(point, i) in document.approach" :key="i" :data-testid="`cv-approach-${i}`">
          <span class="text-white font-medium">{{ point.heading }}</span> — {{ point.detail }}
        </li>
      </ul>
    </section>

    <section v-if="document.roles.length" data-testid="cv-roles">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-2">Experience</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-3">
        <li v-for="(role, i) in document.roles" :key="i" :data-testid="`cv-role-${i}`">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <span class="text-white font-medium">{{ role.title }}</span>
            <span class="text-xs text-white/50">{{ period(role.start, role.end) }}</span>
          </div>
          <div class="text-white/60">
            {{ role.employer }}<span v-if="role.location"> · {{ role.location }}</span>
          </div>
          <ul v-if="role.bullets.length" class="m-0 mt-1 pl-4 flex flex-col gap-0.5">
            <li v-for="(bullet, j) in role.bullets" :key="j">{{ bullet }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.board_roles.length" data-testid="cv-board-roles">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-2">Board positions</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-3">
        <li v-for="(board, i) in document.board_roles" :key="i" :data-testid="`cv-board-role-${i}`">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <span class="text-white font-medium">{{ board.title }}</span>
            <span class="text-xs text-white/50">{{ period(board.start, board.end) }}</span>
          </div>
          <div class="text-white/60">
            {{ board.organisation }}<span v-if="board.description"> · {{ board.description }}</span>
          </div>
          <ul v-if="board.bullets.length" class="m-0 mt-1 pl-4 flex flex-col gap-0.5">
            <li v-for="(bullet, j) in board.bullets" :key="j">{{ bullet }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.projects.length" data-testid="cv-projects">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-2">Projects</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-3">
        <li v-for="(project, i) in document.projects" :key="i" :data-testid="`cv-project-${i}`">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <a
              v-if="project.url"
              :href="project.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-white font-medium underline decoration-white/30"
              >{{ project.name }}</a
            >
            <span v-else class="text-white font-medium">{{ project.name }}</span>
            <span class="text-xs text-white/50">{{ project.period }}</span>
          </div>
          <div class="text-white/60">{{ project.description }}</div>
          <ul v-if="project.bullets.length" class="m-0 mt-1 pl-4 flex flex-col gap-0.5">
            <li v-for="(bullet, j) in project.bullets" :key="j">{{ bullet }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.education.length" data-testid="cv-education">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-2">Education</h3>
      <ol class="list-none p-0 m-0 flex flex-col gap-2">
        <li v-for="(edu, i) in document.education" :key="i">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3">
            <span class="text-white font-medium">{{ edu.degree }}</span>
            <span class="text-xs text-white/50">{{ period(edu.start, edu.end) }}</span>
          </div>
          <div class="text-white/60">{{ edu.institution }}</div>
          <ul v-if="edu.details.length" class="m-0 mt-1 pl-4">
            <li v-for="(detail, j) in edu.details" :key="j">{{ detail }}</li>
          </ul>
        </li>
      </ol>
    </section>

    <section v-if="document.skills.length" data-testid="cv-skills">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-2">Skills</h3>
      <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <template v-for="(group, i) in document.skills" :key="i">
          <dt class="text-white/60">{{ group.category }}</dt>
          <dd class="m-0">{{ group.items.join(', ') }}</dd>
        </template>
      </dl>
    </section>

    <section v-if="document.languages.length" data-testid="cv-languages">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0 mb-1">Languages</h3>
      <p class="m-0">
        <span v-for="(lang, i) in document.languages" :key="i">
          <span v-if="i > 0"> · </span>{{ lang.language
          }}<span v-if="lang.level" class="text-white/50"> ({{ lang.level }})</span>
        </span>
      </p>
    </section>

    <p class="m-0 text-white/50" data-testid="cv-courses-note">{{ CV_COURSES_NOTE }}</p>
  </article>
</template>
